// import "../../assets/css/style2.css";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  listAdminPatients,
  bulkDownloadRecordingAdminPatientsZip,
} from "../../actions/patientActions";
import { toast } from "react-toastify";
import ListHeading from "../list-record-components/list-header/listHeading";
import Table from "../react-table-component/ReactTable";
import { ArrowDownward, ArrowForwardIos } from "@mui/icons-material";
import RecordingAdminPatientFilterCard from "./RecordingAdminPatientFilterCard";
import StatsCard from "./RecordingAdminStats";
import downloadIcon from "../../assets/img/svg/download.svg";
import {
  sumPatientsRecordingsFileSizesBytes,
  formatTotalBytesAsGb,
} from "../../utils/recordingFileSize";
import "./patients.css";

const STUDY = {
  HRI_CS02: "HRI",
  GCU_CS03: "GCU",
  INDIA_INTERNAL: "INDIA INTERNAL",
};

export default function RecordingAdminPatients() {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const token = useSelector((state) => state.loginData.token);
  const patientAdminList = useSelector((state) => state.patientAdminList);

  const { loading, data: data } = patientAdminList;
  const [progress, setProgress] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({});
  const patients = data?.patients || [];

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prevProgress) =>
          prevProgress >= 100 ? 100 : prevProgress + 5,
        );
      }, 500);
      return () => clearInterval(interval);
    };

    simulateProgress();

    dispatch(listAdminPatients(token, {}))
      .then(() => {
        setProgress(100);
      })
      .catch(() => {
        setProgress(100);
      });
  }, [dispatch, token]);

  const [searchField, setSearchField] = useState("");

  const handleSearch = (e) => {
    const searchField = e.target.value.toLocaleLowerCase();
    setSearchField(searchField);
  };

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters || {});
    if (
      filters?.dateFrom ||
      filters?.dateTo ||
      filters?.locationTag ||
      filters?.studyType ||
      filters?.subjectGroup
    ) {
      toast.success("Filter applied successfully!");
    } else {
      toast.info("All filters cleared");
    }
  };

  const filteredPatients = React.useMemo(() => {
    if (!Array.isArray(patients)) return [];

    const searchValue = searchField?.toLowerCase() || "";

    const hasRecordingFilters =
      appliedFilters?.dateFrom ||
      appliedFilters?.dateTo ||
      appliedFilters?.locationTag;
    return patients
      .map((patient) => {
        let filteredRecordings = patient.recordings || [];

        // ✅ Apply recording-level filters
        if (hasRecordingFilters) {
          filteredRecordings = filteredRecordings.filter((rec) => {
            const recDate = new Date(rec.timestamp);

            const from = appliedFilters.dateFrom
              ? new Date(appliedFilters.dateFrom)
              : null;

            const to = appliedFilters.dateTo
              ? new Date(appliedFilters.dateTo)
              : null;

            if (to) to.setHours(23, 59, 59, 999);

            const matchesDate =
              (!from || recDate >= from) && (!to || recDate <= to);

            const matchesLocation =
              !appliedFilters.locationTag ||
              rec.location_tag === appliedFilters.locationTag;

            return matchesDate && matchesLocation;
          });
        }

        return {
          ...patient,
          recordings: filteredRecordings,
          recording_count: filteredRecordings.length,
        };
      })
      .filter((patient) => {
        // ✅ Search filter
        const id = patient?.id_number?.toLowerCase() || "";
        const matchesSearch = id.includes(searchValue);

        // ✅ Study Type (handle "None")
        const matchesStudyType =
          !appliedFilters.studyType ||
          (patient.study_type || "None") === appliedFilters.studyType;

        // ✅ Subject Group (if exists in your data)
        const matchesSubjectGroup =
          !appliedFilters.subjectGroup ||
          patient.subject_group === appliedFilters.subjectGroup;

        // ✅ If recording filters applied → must have recordings
        const hasValidRecordings =
          !hasRecordingFilters || patient.recordings.length > 0;

        return (
          matchesSearch &&
          matchesStudyType &&
          matchesSubjectGroup &&
          hasValidRecordings
        );
      });
  }, [patients, searchField, appliedFilters]);

  const statsData = React.useMemo(() => {
    const subjectCount = filteredPatients.length;
    const recordingCount = filteredPatients.reduce(
      (sum, p) => sum + (p.recordings?.length ?? 0),
      0,
    );
    const studyTypes = new Set(
      filteredPatients
        .map((p) => p.study_type)
        .filter(
          (st) =>
            st &&
            Object.prototype.hasOwnProperty.call(STUDY, st),
        ),
    );
    const exportBytes = sumPatientsRecordingsFileSizesBytes(filteredPatients);

    return [
      { label: "Subjects", value: subjectCount },
      { label: "Recordings", value: recordingCount },
      { label: "Studies", value: studyTypes.size },
      { label: "Export Size", value: formatTotalBytesAsGb(exportBytes) },
    ];
  }, [filteredPatients]);

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);

  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const columns = React.useMemo(() => {
    let baseColumns = [
      {
        Header: "Subject",
        accessor: "id_number",
        Cell: ({ row }) => {
          return (
            <div className="expansion-wrap">
              {row.isExpanded ? (
                <ArrowDownward style={{ fontSize: 12, marginRight: 4 }} />
              ) : (
                <ArrowForwardIos style={{ fontSize: 12, marginRight: 4 }} />
              )}
              {row?.original?.id_number ?? "N/A"}
            </div>
          );
        },
      },

      {
        Header: "Age",
        accessor: "age",
        Cell: ({ row }) => <div>{row.original.age ?? "N/A"}</div>,
      },
      {
        Header: "Gender",
        accessor: "gender",
        Cell: ({ row }) => (
          <div>{row.original.gender === "1" ? "Male" : "Female"}</div>
        ),
      },
      {
        Header: "Group",
        accessor: "group",
        Cell: ({ row }) => <div>{row.original.group ?? "N/A"}</div>,
      },
      {
        Header: "Study",
        accessor: "study",
        Cell: ({ row }) => {
          const rowStudyType = row.original.study_type;
          return <div>{STUDY[rowStudyType] ?? "N/A"}</div>;
        },
      },
      {
        Header: "Recordings",
        accessor: "recordings",
        Cell: ({ row }) => <div>{row.original.recording_count ?? "N/A"}</div>,
      },
      {
        Header: "Date Range",
        accessor: "date_range",
        Cell: ({ row }) => <div>{row.original.visit_date_range ?? "N/A"}</div>,
      },
    ];

    return baseColumns;
  }, [openMenuRowIndex]);

  const handleSelectedRowsChange = (selectedIds) => {
    setSelectedRows(selectedIds);
  };

  const handleBulkZipDownload = async () => {
    if (!selectedRows?.length || !token) return;
    setBulkDownloading(true);
    try {
      const res = await dispatch(
        bulkDownloadRecordingAdminPatientsZip(token, selectedRows),
      );
      if (res?.status && res?.data?.download_url) {
        const link = document.createElement("a");
        link.href = res.data.download_url;
        link.download = res.data.zip_file_name || "patient_recordings.zip";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(
          res.message || "Bulk recording ZIP download URL generated",
        );
        if (res.data.missing_patient_ids?.length) {
          toast.warning(
            `No recordings for patient id(s): ${res.data.missing_patient_ids.join(", ")}`,
          );
        }
        if (res.data.errors?.length) {
          toast.warning(res.data.errors.join("; "));
        }
      } else {
        toast.error(res?.message || "Failed to retrieve bulk download URL");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message;
      toast.error(msg || "Failed to download bulk recordings");
    } finally {
      setBulkDownloading(false);
    }
  };

  return (
    <>
      <section className="patient-list">
        <ListHeading
          title="View Admin Recordings"
          placeholder="Search by subject"
          handleSearch={handleSearch}
          showSearch={false}
          showFilter={false}
          showDownload={false}
        />

        <RecordingAdminPatientFilterCard
          onApplyFilter={handleApplyFilter}
          currentFilters={appliedFilters}
          searchValue={searchField}
          onSearchChange={handleSearch}
        />

        <div className="tw-p-2">
          <StatsCard stats={statsData} />
        </div>

        {selectedRows.length > 0 && (
          <div className="recording-admin-bulk-bar" role="status">
            <div className="recording-admin-bulk-bar__meta">
              <span className="recording-admin-bulk-bar__label">Selection</span>
              <span className="recording-admin-bulk-bar__count">
                {selectedRows.length} subject
                {selectedRows.length !== 1 ? "s" : ""} selected for export
              </span>
            </div>
            <button
              type="button"
              className="recording-admin-bulk-download-btn"
              onClick={handleBulkZipDownload}
              disabled={bulkDownloading}
              title="Download a ZIP of recordings for the selected subjects"
            >
              <img
                src={downloadIcon}
                alt=""
                className="recording-admin-bulk-download-btn__icon"
                aria-hidden
              />
              {bulkDownloading ? "Preparing download…" : "Download ZIP"}
            </button>
          </div>
        )}

        <Table
          columns={columns}
          data={filteredPatients ?? []}
          loading={loading}
          tableOf="RecordingAdminPatients"
          progress={progress}
          onSelectedRowsChange={handleSelectedRowsChange}
        />
      </section>
    </>
  );
}
